import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Clock, Loader2, PauseCircle, Send, ShieldX } from 'lucide-react';
import clsx from 'clsx';
import type { AccessRequestState } from '@idas/shared';
import { api, ApiError } from '../lib/api';
import { IntervalLogo } from '../components/ui/IntervalLogo';

/**
 * Where someone lands when they click "Data Access" in the portal but do not
 * have access yet.
 *
 * Deliberately not an error page. Not having access on your first visit is
 * the normal path, and the click itself has already filed the request — so
 * this screen's job is to say what happens next, not to tell someone off.
 *
 * Public: the visitor has no session by definition. Everything shown comes
 * from the redirect's own parameters and describes only the visitor's own
 * status, so there is nothing here they could not already see.
 */

const PURPOSE_PRESETS = [
  'I produce the monthly reporting pack and need to query the source figures.',
  'I need to reconcile records against another system as part of my role.',
  'My manager has asked me to analyse this data for a specific project.',
];

const MIN_PURPOSE = 15;

/**
 * Lets someone say why they need access, before anyone decides.
 *
 * Submitting is what actually notifies the administrators — a mail sent the
 * instant somebody pressed a button, with nothing in it to read, only teaches
 * people to ignore the next one.
 */
function PurposeForm({ token, existing }: { token: string; existing: string | null }) {
  const [purpose, setPurpose] = useState(existing ?? '');
  const [attempted, setAttempted] = useState(false);
  const [sent, setSent] = useState(Boolean(existing));

  const submit = useMutation({
    mutationFn: () => api.auth.submitAccessPurpose(token, purpose.trim()),
    onSuccess: () => setSent(true),
  });

  const tooShort = purpose.trim().length < MIN_PURPOSE;

  if (sent && !submit.isPending) {
    return (
      <div className="mt-4 rounded-md border border-ok/30 bg-ok/5 p-3">
        <p className="mb-1 flex items-center gap-1.5 text-2xs font-medium text-ok">
          <CheckCircle2 className="h-3 w-3" />
          Your administrators have been told
        </p>
        <p className="text-2xs leading-relaxed text-ink">“{purpose.trim()}”</p>
        <button
          className="btn mt-2.5"
          onClick={() => {
            setSent(false);
            submit.reset();
          }}
        >
          Edit this
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-md border border-edge bg-surface-sunken p-3">
      <label htmlFor="purpose" className="mb-1.5 block text-2xs font-medium text-ink">
        What do you need the data for? <span className="text-danger">*</span>
      </label>
      <p className="mb-2 text-2xs leading-relaxed text-ink-muted">
        This goes straight to whoever approves it. A specific answer is approved faster than
        “reporting”.
      </p>

      <div className="mb-1.5 flex flex-wrap gap-1">
        {PURPOSE_PRESETS.map((preset) => (
          <button
            key={preset}
            className="chip border-edge bg-surface text-left text-ink-muted hover:border-accent hover:text-accent"
            onClick={() => setPurpose(preset)}
          >
            {preset.slice(0, 42)}…
          </button>
        ))}
      </div>

      <textarea
        id="purpose"
        required
        aria-required="true"
        className={clsx(
          'input min-h-[72px] resize-y leading-relaxed',
          attempted && tooShort && 'border-warn',
        )}
        placeholder="e.g. I prepare the monthly enrolment report and need the batch and attendance figures."
        value={purpose}
        onChange={(event) => setPurpose(event.target.value)}
      />

      {attempted && tooShort && (
        <p className="mt-1 text-2xs text-warn">
          At least {MIN_PURPOSE} characters ({purpose.trim().length} so far).
        </p>
      )}
      {submit.error && (
        <p className="mt-1.5 text-2xs text-danger">
          {submit.error instanceof ApiError ? submit.error.message : 'Could not send that.'}
        </p>
      )}

      <button
        className="btn btn-primary mt-2.5"
        disabled={submit.isPending}
        onClick={() => (tooShort ? setAttempted(true) : submit.mutate())}
      >
        {submit.isPending ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Sending…
          </>
        ) : (
          <>
            <Send className="h-3.5 w-3.5" />
            Send to my administrator
          </>
        )}
      </button>
    </div>
  );
}

const STAGES = [
  { key: 'requested', label: 'Request submitted' },
  { key: 'review', label: 'Administrator review' },
  { key: 'decision', label: 'Access granted' },
] as const;

/**
 * Copy per outcome.
 *
 * Four states, not two. Being suspended after months of use is a different
 * event from having a request refused, and telling someone "your request was
 * not approved" — above the note that approved it — is both wrong and
 * confusing. `noteLabel` matters as much as the heading: the reason shown for
 * a suspension is the suspension's reason, not the original decision's.
 */
const OUTCOMES: Record<
  string,
  {
    tone: 'wait' | 'bad';
    title: string;
    lead: string;
    noteLabel: string;
    footer: string;
    /** Whether the progress track still applies. */
    showStages: boolean;
  }
> = {
  PENDING: {
    tone: 'wait',
    title: 'Your request has been submitted',
    lead: 'You do not need to do anything else. An administrator will review it, and you will be able to sign in from the employee portal once it is approved.',
    noteLabel: 'Note from your administrator',
    footer:
      'Approval happens in this system rather than in the employee portal, so it is a separate step from your normal account.',
    showStages: true,
  },
  REJECTED: {
    tone: 'bad',
    title: 'Access was not approved',
    lead: 'An administrator reviewed your request and did not grant access.',
    noteLabel: 'Reason given',
    footer:
      'If you believe this is wrong, speak to your administrator — access is granted here, not in the employee portal.',
    showStages: true,
  },
  SUSPENDED: {
    tone: 'wait',
    title: 'Your access has been paused',
    lead: 'You had access to the console, and an administrator has paused it. Your account and history are intact — it can be restored without you applying again.',
    noteLabel: 'Reason given',
    footer:
      'Speak to your administrator to have it restored. You do not need to submit a new request.',
    showStages: false,
  },
  REVOKED: {
    tone: 'bad',
    title: 'Your access has been withdrawn',
    lead: 'You had access to the console and an administrator has withdrawn it.',
    noteLabel: 'Reason given',
    footer:
      'If you need access again, speak to your administrator — access is granted here, not in the employee portal.',
    showStages: false,
  },
};

export function AccessRequestPage() {
  const [params] = useSearchParams();
  const state = (params.get('state') ?? 'PENDING') as AccessRequestState;
  const name = params.get('name');
  const email = params.get('email');
  const note = params.get('note');
  const requestedAt = params.get('requestedAt');
  const purpose = params.get('purpose');
  const requestToken = params.get('rt');
  const changedBy = params.get('by');
  const changedAt = params.get('at');

  const outcome = OUTCOMES[state] ?? OUTCOMES.PENDING!;
  const bad = outcome.tone === 'bad';
  const rejected = state === 'REJECTED';
  const currentStage = rejected ? 1 : 2;

  return (
    <div className="flex h-full items-center justify-center bg-surface p-6">
      <div className="w-full max-w-lg animate-slide-up">
        <div className="mb-6 flex justify-center">
          <IntervalLogo height={34} />
        </div>

        <div className="panel rounded-xl p-7">
        <div
          className={clsx(
            'mb-5 inline-flex h-10 w-10 items-center justify-center rounded-lg',
            bad ? 'bg-danger/10 text-danger' : 'bg-accent/10 text-accent',
          )}
        >
          {bad ? (
            <ShieldX className="h-5 w-5" strokeWidth={1.75} />
          ) : state === 'SUSPENDED' ? (
            <PauseCircle className="h-5 w-5" strokeWidth={1.75} />
          ) : (
            <Clock className="h-5 w-5" strokeWidth={1.75} />
          )}
        </div>

        <h1 className="text-base font-semibold text-ink">{outcome.title}</h1>

        <p className="mt-2 text-sm leading-relaxed text-ink-muted">{outcome.lead}</p>

        {changedBy && (
          <p className="mt-1.5 text-2xs text-ink-faint">
            By {changedBy}
            {changedAt ? ` on ${new Date(changedAt).toLocaleString()}` : ''}
          </p>
        )}

        {/* The purpose form. Shown while a decision is outstanding, because
            an administrator approving a request with no stated reason is just
            processing paperwork — and because the person asking is the only
            one who knows why. */}
        {!rejected && requestToken && <PurposeForm token={requestToken} existing={purpose} />}

        {(name || email) && (
          <div className="mt-4 rounded-md border border-edge bg-surface-sunken p-3">
            <p className="text-2xs text-ink-faint">Requested for</p>
            <p className="text-2xs font-medium text-ink">{name}</p>
            {email && <p className="text-2xs text-ink-muted">{email}</p>}
            {requestedAt && (
              <p className="mt-1 text-2xs text-ink-faint">
                Submitted {new Date(requestedAt).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* The three stages, so "what happens next" is visible rather than
            something the reader has to infer from a sentence.
            Hidden once access has been paused or withdrawn — that is not a
            point on this track, and drawing it there implies the request is
            still being considered. */}
        {outcome.showStages && (
        <ol className="mt-5 space-y-0">
          {STAGES.map((stage, index) => {
            const done = index < currentStage;
            const failed = rejected && index === currentStage;
            const active = !rejected && index === currentStage;

            return (
              <li key={stage.key} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className={clsx(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-2xs',
                      done && 'border-ok bg-ok text-white',
                      failed && 'border-danger bg-danger text-white',
                      active && 'border-accent text-accent',
                      !done && !failed && !active && 'border-edge-strong text-ink-faint',
                    )}
                  >
                    {done ? (
                      <CheckCircle2 className="h-3 w-3" />
                    ) : failed ? (
                      <ShieldX className="h-3 w-3" />
                    ) : (
                      index + 1
                    )}
                  </span>
                  {index < STAGES.length - 1 && (
                    <span
                      className={clsx('w-px flex-1', done ? 'bg-ok/40' : 'bg-edge')}
                      style={{ minHeight: 22 }}
                    />
                  )}
                </div>
                <div className="pb-4">
                  <p
                    className={clsx(
                      'text-2xs font-medium',
                      done || failed || active ? 'text-ink' : 'text-ink-faint',
                    )}
                  >
                    {failed && index === 2 ? 'Access refused' : stage.label}
                  </p>
                  {active && (
                    <p className="text-2xs text-ink-muted">Waiting for a decision.</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        )}

        {note && (
          <div
            className={clsx(
              'mt-5 rounded-md border p-3',
              bad ? 'border-danger/30 bg-danger/5' : 'border-warn/30 bg-warn/5',
            )}
          >
            <p className="mb-1 text-2xs font-medium text-ink-muted">{outcome.noteLabel}</p>
            <p className="text-2xs leading-relaxed text-ink">{note}</p>
          </div>
        )}

        <p className="mt-5 border-t border-edge pt-4 text-2xs leading-relaxed text-ink-faint">
          {outcome.footer}
        </p>
        </div>
      </div>
    </div>
  );
}
